<template>
  <div style="width: 100%;" v-loading.body="loading" element-loading-text="Loading...">
    <el-row type="flex" align="middle" justify="end" style="border-bottom: 1px solid lightgrey;">
      <HeaderInfoText :colspan="12" title="On-Demand Positive Pay" />
      <el-col :span="12">
        <el-row type="flex" align="middle" justify="end">
          <el-button plain @click="generateReport" :disabled="disableReportRun" style="margin: 0 15px;">
            Create Export
          </el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle" justify="end" class="headerRow">
      <el-col>
        <el-row type="flex" align="middle" justify="start" style="font-size: 1.2em;">
          Export Options
        </el-row>
      </el-col>
      <el-col>
        <el-row type="flex" align="middle" justify="end">
          <p><sup>*</sup> Denotes a required field</p>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle">
      <el-col :span="12">
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            <div style="display: inline-flex">
              Bank Account*
            </div>
          </el-col>
          <el-col :span="18">
            <el-select v-model="selectedCodes.bank__in" :multiple="false" placeholder="Bank Account" @change="updateFormat" required>
              <el-option
                v-for="opt in allCodes.bank__in"
                :key="opt.id"
                :label="opt.name"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>

        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            Format
          </el-col>
          <el-col :span="18">
            <el-select v-model="reportOptions.format" required>
              <el-option
                v-for="opt in reportOutputFormats"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value">
              </el-option>
            </el-select>
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="6">
            <div style="display: inline-flex">
              Re-Export Data
            </div>
          </el-col>
          <el-col :span="18">
            <el-select v-model="selectedCodes.export" :multiple="false" placeholder="Leave Blank to Create a New Export">
              <el-option label="Create a New Export" value="" placeholder="Leave Blank to Create a New Export"/>
              <el-option
                v-for="opt in filteredExports"
                :key="opt.id"
                :label="new Date(opt.attributes.timestamp).toLocaleDateString() + ' ' + new Date(opt.attributes.timestamp).toLocaleTimeString() + ' - ' + opt.get('bank.name')"
                :value="opt.id">
              </el-option>
            </el-select>
          </el-col>
        </el-row>

        <el-row
          type="flex"
          align="middle"
          justify="start"
          class="headerRow"
          v-if="selectedOption === 'payment_type' && Object.keys(includeOptions).length">
          <el-col :span="24">
            <el-row type="flex" align="middle">
              <el-col :span="6">
                Payment Type
              </el-col>
              <el-col :span="18">
                <el-row type="flex" align="middle" justify="start" v-for="(option, key) in includeOptions" :key="key">
                  <el-checkbox v-model="selectedCodes[key]" />
                  <span style="margin: 6px;">{{ option.name }}</span>
                </el-row>
              </el-col>
            </el-row>
          </el-col>
        </el-row>
      </el-col>
    </el-row>

    <el-row v-if="showJson">
      <el-col>
        <textarea id="output" style="width: 100%; height: 500px !important; font-family: 'Courier New'" v-model="prettyJson"></textarea>
      </el-col>
    </el-row>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import { mapGetters } from 'vuex'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import store from '@/store/store'
  import { positivePayMixins } from '@/Mixins/PositivePay'
  import { aggregateMixins } from '@/Mixins/Aggregate'
  import { reportMixins } from '@/Mixins/Reports'
  import { getSimpleDate } from '@/utils/helpers/FormatDate'
  import { TransactionExportResource } from '@sigmacloud/sigma-client/dist/resources'

  export default defineComponent({
    name: 'PositivePay',
    mixins: [ positivePayMixins, aggregateMixins, reportMixins ],
    components: {
      HeaderInfoText
    },
    data () {
      return {
        reportOptions: {    // Options to run report.
          format: ''        // Positive Pay Format
        },
        allCodes: {         // Show codes, e.g. accounts, series, locations, sets, etc.
          bank__in: [],
          exports: []
        },
        selectedCodes: {    // User selected codes from choices of allCodes above
          bank__in: [],
          export: undefined
        },
        disableReportRun: false, // Disable run report button if false
        loading: false,
        // selectedOption: 'all',  // Future functionality
        prettyJson: undefined,
        showJson: false,
        reportOutputFormats: [
          // Can store a default value in Bank "postitivepay_host" property
          { value: 'cds', label: 'CDS Delimited Text File' },
          { value: 'cnb', label: 'City National Bank CSV' },
          { value: 'cnbFixed', label: 'City National Bank Fixed Width Format' },
          { value: 'arpFixed', label: 'JP Morgan Chase ARP' },
          { value: 'union', label: 'Union Bank' },
          { value: 'wfmanual', label: 'Wells Fargo CEO Fraud Manager' }
        ]
      }
    },
    methods: {
      /**
       * Performs basic validation, e.g. are required fields selected
       * @return {boolean}
       */
      checkRequiredElementFields () {
        /* Add "required" to el-select and "required-multiselect" to multiple selectors, e.g.
          Single select:
            <el-select v-model="bank" :multiple="false" placeholder="Bank Account" required>

          Multiple select:
            <el-select v-model="company" :multiple="true"  placeholder="All" required-multiselect>
        */
        let errorCount = 0
        const requiredInputFields = this.$el.querySelectorAll('[required] input')             // Works for el-select (non-multiple)/input tags
        for (const field of requiredInputFields) {
          field.classList.remove('form-error')
          if (field && !field.value) {
            field.classList.add('form-error')
            errorCount++
          }
        }
        const requiredMultiSelectFields = this.$el.querySelectorAll('[required-multiselect]') // Need this for multiple-select el-select boxes
        for (const field of requiredMultiSelectFields) {
          field.querySelector('input').classList.remove('form-error')
          if (field && field.querySelectorAll('.el-select__tags-text').length === 0) {
            field.querySelector('input').classList.add('form-error')
            errorCount++
          }
        }
        return !errorCount
      },

      /**
       * Finds matching checks and APs to export, and then get thte data to format into the proper formats.
       * @return {Promise<boolean>}
       */
      async generateReport () {
        if (!this.checkRequiredElementFields()) {
          return false
        }
        this.loading = true
        this.disableReportRun = true

        try {
          // Which Format
          let formatter, extension
          switch (this.reportOptions.format) {
            case 'cnb':
              formatter = 'createCnb'
              extension = 'csv'
              break

            case 'cnbFixed':
              formatter = 'createCnbFixed'
              extension = 'txt'
              break
            case 'arpFixed':
              formatter = 'createArpFixed'
              extension = 'txt'
              break

            case 'union':
              formatter = 'createUnion'
              extension = 'csv'
              break

            case 'wfmanual':
              formatter = 'createArpWellsManual'
              extension = 'csv'
              break
            case 'cds':
              formatter = 'createCds'
              extension = 'txt'
              break

            default:
              formatter = 'createCnb'
              extension = 'csv'
          }

          // Get Check/AP Records
          this.getPositivePayIds([ 'CHECK', 'CHECK_MANUAL' ], this.selectedBank, this.selectedCodes.export)
            .then(transactions => {
              // Combine and iterate into our formatter
              const combined = [ ...transactions.papercheckTrx, ...transactions.accountsPayableTrx, ...transactions.voidTrx ]
              const data = this[formatter](combined, this.selectedBank)

              // Create Output
              this.prettyJson = data
              this.createNotification('Success', 'File is Ready. ', 'success', false, extension, 'pospay-' + this.selectedBank.code + '-' + getSimpleDate(new Date()))
              return true
            })
            .catch(error => {
              this.createNotification('Info', error, 'info')
              console.log(error)
              return error
            })
            .finally(() => {
              this.loading = false
              this.disableReportRun = false
            })
        } catch (error) {
          console.log(error)
          return error
        }
      },
      updateFormat () {
        if (this.selectedCodes.bank__in) {
          this.reportOptions.format = this.selectedBank.positivepay_host || null
        }
      }
    },
    computed: {
      ...mapGetters({
        getReportFormFields: 'getReportFormFields',
        getBankById: 'getBankById',
        getVendorById: 'getVendorById'
      }),
      resources () {
        return {
          TransactionExportResource
        }
      },
      includeOptions () {
        const ret = {}
        for (let key in this.allCodes) {
          if (!key.includes('include_')) {
            continue
          }
          ret[key] = { ...this.allCodes[key] }
        }
        return ret
      },
      selectedBank () {
        if (!this.selectedCodes.bank__in) {
          return null
        } else {
          const bank = this.getBankById(this.selectedCodes.bank__in)
          return bank
        }
      },
      filteredExports () {
        if (!this.selectedBank || !this.selectedBank.id || !this.allCodes.exports) {
          return []
        } else {
          const exports = this.allCodes.exports.filter((exp) => exp.attributes.bank === this.selectedBank.id)
          return exports.sort((a, b) => b.id - a.id)
        }
      }
    },
    // Load bank
    created () {
      this.loading = true
      store.dispatch('fetchBanksWithQuery', { page_size: 200 })
      .then((response) => {
        if (response.entities.banks && typeof response.entities.banks === 'object') {
          for (const i in response.entities.banks) {
            this.allCodes.bank__in.push(response.entities.banks[i])
          }
          if (this.allCodes.bank__in.length === 1) {
            this.selectedCodes.bank__in = (this.allCodes.bank__in[0] || {}).id || null
            this.updateFormat()
          }
        } else {
          throw new Error('Unable to fetch report options')
        }
      })
      .catch(error => {
        console.log(error)
      })
      .finally(() => {
        this.loading = false
        this.disableReportRun = false
      })
    },
    async mounted () {
      let transactionExportResources = await this.resources.TransactionExportResource.all()
      for (const i in transactionExportResources) {
        const exportResource = transactionExportResources[i]
        await exportResource.resolveRelated({ managers: [ 'bank' ] })
      }
      this.allCodes.exports = transactionExportResources
    }
  })

</script>
<style scoped>
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow { padding: 15px 20px 0px; }
  .m-right-20 { margin-right: 20px; }
  .m-top-50 { margin-top: 50px; }
  .date {
    display: inline-flex;
  }
  .field {
    margin: 0 6px;
  }
</style>
<style>
  .el-select { display: block; }
  .el-transfer-panel { width:36%; max-width: 350px }
  .el-transfer-panel__body { min-height: 400px }
  .el-transfer-panel__list { min-height: 360px }
  .form-error {background-color: #F08080; font-weight: bold; color: #000000; }
  /* border: 1px solid #d1dbe5; box-shadow: 0 2px 4px rgba(0,0,0,.12),0 0 6px rgba(0,0,0,.04); display: inline-block; width: 30%; position: relative; height: 500px; */
</style>
